import React, { Component } from "react";
import PropTypes from "prop-types";
import { intlShape } from "@ai360/core/intl";

import { Loader } from "~/core";
import { ImportWizardHeader } from "./import-wizard-header/import-wizard-header";
import { ImportWizardPanel } from "./import-wizard-panel/import-wizard-panel";
import { ImportWizardFooter } from "./import-wizard-footer/import-wizard-footer";
import { FieldResurfacePrompt } from "../../../../common";

import { ImportTypes, PanelTypes } from "../actions";
import { messages } from "../i18n-messages";

import "./import-wizard.css";

interface IStep {
    id: number;
    label: string;
    panelType: string;
    options: any;
}

const _getStepList = (formatMessage, importType, isSingleStep, isUpdate): IStep[] => {
    switch (importType.name.toLowerCase()) {
        case ImportTypes.FIELD_BOUNDARY: {
            const message = isUpdate
                ? messages.fieldBoundaryUpdateText
                : messages.fieldBoundaryImportText;
            return [
                {
                    id: 0,
                    label: formatMessage(message),
                    panelType: PanelTypes.FIELD_BOUNDARY_PANEL,
                    options: {},
                },
            ];
        }
        case ImportTypes.SAMPLING_SOIL:
        case ImportTypes.SAMPLING_NEMATODE:
        case ImportTypes.SAMPLING_TISSUE: {
            return isSingleStep
                ? [
                      {
                          id: 0,
                          label: formatMessage(
                              messages.importWizardCreateEventsSamplingCentroidText
                          ),
                          panelType: PanelTypes.EVENTS_PANEL,
                          options: {
                              ignoreSelectedField: true,
                              isFilteringVisible: true,
                              isSingleStep: true,
                          },
                      },
                  ]
                : [
                      {
                          id: 0,
                          label: formatMessage(messages.importWizardFilesText),
                          panelType: PanelTypes.FILES_PANEL,
                          options: {
                              ignoreSelectedField: true,
                              isFilteringVisible: false,
                          },
                      },
                      {
                          id: 1,
                          label: formatMessage(messages.importWizardFieldsText),
                          panelType: PanelTypes.FIELDS_PANEL,
                          options: {
                              ignoreSelectedField: false,
                              isFilteringVisible: true,
                          },
                      },
                      {
                          id: 2,
                          label: formatMessage(messages.importWizardEventsText),
                          panelType: PanelTypes.EVENTS_PANEL,
                          options: {
                              ignoreSelectedField: true,
                              isFilteringVisible: true,
                          },
                      },
                  ];
        }
        case ImportTypes.MANAGEMENT_AREA: {
            return [
                {
                    id: 0,
                    label: formatMessage(messages.importWizardFilesText),
                    panelType: PanelTypes.FILES_PANEL,
                    options: {
                        ignoreSelectedField: true,
                        isFilteringVisible: false,
                    },
                },
                {
                    id: 1,
                    label: formatMessage(messages.importWizardFieldsText),
                    panelType: PanelTypes.FIELDS_PANEL,
                    options: {
                        ignoreSelectedField: false,
                        isFilteringVisible: true,
                    },
                },
                {
                    id: 2,
                    label: formatMessage(messages.importWizardLayersLabelText),
                    panelType: PanelTypes.MANAGEMENT_AREA_PANEL,
                    options: {
                        ignoreSelectedField: true,
                        isFilteringVisible: true,
                    },
                },
            ];
        }
        default: {
            return [
                {
                    id: 0,
                    label: formatMessage(messages.importWizardFilesText),
                    panelType: PanelTypes.FILES_PANEL,
                    options: {
                        ignoreSelectedField: true,
                        isFilteringVisible: false,
                    },
                },
                {
                    id: 1,
                    label: formatMessage(messages.importWizardFieldsText),
                    panelType: PanelTypes.FIELDS_PANEL,
                    options: {
                        ignoreSelectedField: false,
                        isFilteringVisible: true,
                    },
                },
                {
                    id: 2,
                    label:
                        importType.name.toLowerCase() !== ImportTypes.IMAGERY
                            ? formatMessage(messages.importWizardEventsText)
                            : formatMessage(messages.importWizardLayersText, {
                                  count: 2,
                              }),
                    panelType: PanelTypes.EVENTS_PANEL,
                    options: {
                        ignoreSelectedField: true,
                        isFilteringVisible: true,
                    },
                },
            ];
        }
    }
};
const _getSubmitLabel = (
    formatMessage,
    importType,
    importFieldList,
    eventStripCount = 0,
    isResultsTemplate = false
): string => {
    switch (importType.name.toLowerCase()) {
        case ImportTypes.FIELD_BOUNDARY: {
            const isUpdate = importFieldList.length > 0 ? importFieldList[0].isUpdate : false;
            const message = isUpdate
                ? messages.importWizardUpdateFieldText
                : messages.importWizardAddFieldText;
            return formatMessage(message, { count: importFieldList.length });
        }
        case ImportTypes.IMAGERY:
            return formatMessage(messages.importWizardCreateLayersText, {
                count: eventStripCount,
            });
        case ImportTypes.MANAGEMENT_AREA:
            return formatMessage(messages.importWizardCreateLayersText, {
                count: eventStripCount,
            });
        default:
            return formatMessage(
                isResultsTemplate
                    ? messages.importWizardImportText
                    : messages.importWizardCreateEventsText,
                { count: eventStripCount }
            );
    }
};
const _getActiveStep = (stepId, stepList: IStep[]) => {
    return stepList.find((step) => {
        return step.id === stepId;
    });
};

export interface IImportWizardProps {
    changeStep: (index: number) => void;
    closeImportWizard: (isCancel: boolean) => void;
    currentStep: number;
    eventStripCount: number | null;
    fieldsToResurface: string[];
    hasSubmitted: boolean;
    ignoreSelectedField: boolean;
    isFilteringVisible: boolean;
    isFieldMatchingComplete: boolean;
    isFieldMatchingLoading: boolean;
    isLoading: boolean;
    importTemplates: any | null;
    isValidating: boolean;
    isResultsTemplate: boolean;
    importFieldStripData: any[];
    importFieldList: any[];
    importFileGuidList: any[] | null;
    importFiles: any | null;
    importType: any;
    setIgnoreSelectedField: (value: boolean) => void;
    setIsFilteringVisible: (value: boolean) => void;
    setShowImportPoints: (visible: boolean) => void;
    showImportPoints: boolean;
    setFieldBoundarySubmitLoading: (value: boolean) => void;
    submitFieldBoundary: () => void;
    validateImportEvent: () => void;
    intl: intlShape;
    loadDetailPage: () => void;
    onFetchResurfaceEvents: (fieldGuids: string[], keepExistingEvents: boolean) => void;
    onSetFieldEditsChanged: () => void;
    onSetEventsToResurface: (eventsToResurface: string[]) => void;
    resurfaceEvents: any[];
}

interface IState {
    canSubmit: boolean;
    canContinue: boolean;
    importAnalysisLayers: boolean;
    stepList: IStep[];
    showResurfacePrompt: boolean;
}

export class ImportWizard_ extends Component<IImportWizardProps, IState> {
    static propTypes = {
        changeStep: PropTypes.func.isRequired,
        closeImportWizard: PropTypes.func.isRequired,
        currentStep: PropTypes.number.isRequired,
        eventStripCount: PropTypes.number,
        hasSubmitted: PropTypes.bool.isRequired,
        ignoreSelectedField: PropTypes.bool.isRequired,
        isFilteringVisible: PropTypes.bool.isRequired,
        isFieldMatchingComplete: PropTypes.bool.isRequired,
        isFieldMatchingLoading: PropTypes.bool.isRequired,
        isLoading: PropTypes.bool.isRequired,
        importTemplates: PropTypes.object,
        isValidating: PropTypes.bool.isRequired,
        importFieldStripData: PropTypes.array.isRequired,
        importFieldList: PropTypes.array.isRequired,
        importFileGuidList: PropTypes.array,
        importFiles: PropTypes.object,
        importType: PropTypes.object.isRequired,
        setIgnoreSelectedField: PropTypes.func.isRequired,
        setIsFilteringVisible: PropTypes.func.isRequired,
        setShowImportPoints: PropTypes.func.isRequired,
        showImportPoints: PropTypes.bool.isRequired,
        setFieldBoundarySubmitLoading: PropTypes.func.isRequired,
        submitFieldBoundary: PropTypes.func.isRequired,
        validateImportEvent: PropTypes.func.isRequired,
        intl: intlShape.isRequired,
        loadDetailPage: PropTypes.func,
        resurfaceEvents: PropTypes.array,
    };

    constructor(props: IImportWizardProps) {
        super(props);

        this.state = {
            canSubmit: false,
            canContinue: false,
            stepList: _getStepList(
                props.intl.formatMessage,
                props.importType,
                this._getIsSingleStep(),
                this._getIsUpdate()
            ),
            showResurfacePrompt: false,
            importAnalysisLayers: false,
        };

        this._onResurfacePromptCanceled = this._onResurfacePromptCanceled.bind(this);
        this._onResurfacePromptClosed = this._onResurfacePromptClosed.bind(this);
    }

    _getIsUpdate = () => {
        const { importFieldList } = this.props;
        return importFieldList?.length > 0 ? importFieldList[0].isUpdate : false;
    };

    _getIsSingleStep = () => {
        const { importFileGuidList, importFiles, importTemplates, importType } = this.props;
        const applicableImportTypes = [
            ImportTypes.SAMPLING_SOIL,
            ImportTypes.SAMPLING_TISSUE,
            ImportTypes.SAMPLING_NEMATODE,
        ];
        if (!applicableImportTypes.includes(importType.name.toLowerCase())) {
            return false;
        }

        for (const guid of importFileGuidList) {
            const targetFile = importFiles.get(guid);
            if (!targetFile) {
                continue;
            }
            const targetTemplate = importTemplates.get(targetFile.templateGuid);
            if (targetTemplate.isNameMatch) {
                //Some templates only require one import wizard step
                return true;
            }
        }
        return false;
    };

    onSubmitClick = () => {
        if (this.state.canSubmit) {
            this.setState({
                canSubmit: false,
            });
            switch (this.props.importType.name.toLowerCase()) {
                case ImportTypes.FIELD_BOUNDARY:
                    this.updateFieldBoundary();
                    break;
                case ImportTypes.MANAGEMENT_AREA:
                    this.setState({
                        importAnalysisLayers: true,
                    });
                    break;
                default:
                    this.props.validateImportEvent();
            }
        }
    };

    updateFieldBoundary = () => {
        const {
            fieldsToResurface,
            resurfaceEvents,
            onSetFieldEditsChanged,
            setFieldBoundarySubmitLoading,
        } = this.props;
        if (fieldsToResurface && fieldsToResurface.length > 0) {
            setFieldBoundarySubmitLoading(true);
            onSetFieldEditsChanged();
            this.props.onFetchResurfaceEvents(fieldsToResurface, true);
            if (!this.state.showResurfacePrompt && resurfaceEvents) {
                this.setState({
                    showResurfacePrompt: true,
                });
            }
        } else {
            this.props.submitFieldBoundary();
        }
    };

    updateCanContinue = (canContinue: boolean) => {
        if (this.state.canContinue !== canContinue) {
            this.setState({
                canContinue,
            });
        }
    };
    updateCanSubmit = (canSubmit: boolean) => {
        if (this.state.canSubmit !== canSubmit) {
            this.setState({
                canSubmit,
            });
        }
    };

    _onResurfacePromptClosed = (resurfaceEventResult) => {
        this.props.onSetEventsToResurface(resurfaceEventResult);
        this.props.setFieldBoundarySubmitLoading(false);
    };

    _onResurfacePromptCanceled = () => {
        this.props.setFieldBoundarySubmitLoading(false);
        this.setState({ showResurfacePrompt: false });
    };

    UNSAFE_componentWillReceiveProps(nextProps: IImportWizardProps) {
        if (
            nextProps.importType !== this.props.importType ||
            (nextProps.importType.name.toLowerCase() === ImportTypes.FIELD_BOUNDARY &&
                this._getIsUpdate())
        ) {
            this.setState({
                stepList: _getStepList(
                    this.props.intl.formatMessage,
                    nextProps.importType,
                    this._getIsSingleStep(),
                    this._getIsUpdate()
                ),
            });
        }
        if (nextProps.currentStep !== this.props.currentStep) {
            this.setState({
                importAnalysisLayers: false,
            });
            this.updateCanContinue(false);
            const activeStep = _getActiveStep(nextProps.currentStep, this.state.stepList);

            if (activeStep.options) {
                if (activeStep.options.ignoreSelectedField !== nextProps.ignoreSelectedField) {
                    this.props.setIgnoreSelectedField(activeStep.options.ignoreSelectedField);
                }
                if (activeStep.options.isFilteringVisible !== nextProps.isFilteringVisible) {
                    this.props.setIsFilteringVisible(activeStep.options.isFilteringVisible);
                }
            }
        }
        if (
            nextProps.importType.name.toLowerCase() === ImportTypes.FIELD_BOUNDARY &&
            nextProps.resurfaceEvents?.length !== this.props.resurfaceEvents?.length
        ) {
            this.setState({
                showResurfacePrompt: nextProps.resurfaceEvents?.length > 0,
            });
            if (nextProps.resurfaceEvents?.length === 0) {
                this.props.submitFieldBoundary();
            }
        }

        if (
            nextProps.importType.name.toLowerCase() === ImportTypes.FIELD_BOUNDARY &&
            nextProps.resurfaceEvents?.length !== this.props.resurfaceEvents?.length &&
            nextProps.resurfaceEvents === null
        ) {
            this.props.submitFieldBoundary();
        }
    }

    render() {
        const {
            changeStep,
            closeImportWizard,
            currentStep,
            eventStripCount,
            hasSubmitted,
            importFieldStripData,
            importFieldList,
            importType,
            isFieldMatchingComplete,
            isFieldMatchingLoading,
            isLoading,
            isValidating,
            setShowImportPoints,
            showImportPoints,
            isResultsTemplate,
            resurfaceEvents,
        } = this.props;
        const { canContinue, canSubmit, stepList } = this.state;
        const { formatMessage } = this.props.intl;
        const submitLabel = _getSubmitLabel(
            formatMessage,
            importType,
            importFieldList,
            eventStripCount,
            isResultsTemplate
        );
        const activeStep = _getActiveStep(currentStep, stepList);
        const fieldPanelActive = activeStep.panelType === PanelTypes.FIELDS_PANEL;
        const isSingleStep = this._getIsSingleStep();
        const fieldResurfaceProps = {
            resurfaceEvents: resurfaceEvents,
            isOpen: this.state.showResurfacePrompt,
            onClose: (resurfaceEventDetails) =>
                this._onResurfacePromptClosed(resurfaceEventDetails),
            onCancel: this._onResurfacePromptCanceled,
            isImportUpdate: true,
        };
        return (
            <React.Fragment>
                <div className="import-wizard">
                    {isLoading ||
                    (isFieldMatchingLoading && (fieldPanelActive || isSingleStep)) ||
                    isValidating ? (
                        <Loader />
                    ) : null}
                    <ImportWizardHeader
                        currentStep={currentStep}
                        importFieldStripData={importFieldStripData}
                        importType={importType.name.toLowerCase()}
                        stepList={stepList}
                        setShowImportPoints={setShowImportPoints}
                        showImportPoints={showImportPoints}
                        isFieldMatchingComplete={isFieldMatchingComplete}
                    />
                    <ImportWizardPanel
                        canContinue={canContinue}
                        canSubmit={canSubmit}
                        importAnalysisLayers={this.state.importAnalysisLayers}
                        panelType={activeStep.panelType}
                        updateCanContinue={(c: boolean) => this.updateCanContinue(c)}
                        updateCanSubmit={(c: boolean) => this.updateCanSubmit(c)}
                        loadDetailPage={this.props.loadDetailPage}
                        importType={importType}
                    />
                    <ImportWizardFooter
                        canContinue={canContinue}
                        canSubmit={canSubmit}
                        currentStep={currentStep}
                        hasSubmitted={hasSubmitted}
                        importType={importType.name.toLowerCase()}
                        stepList={stepList}
                        submitLabel={submitLabel}
                        onBackClick={() => changeStep(currentStep - 1)}
                        onNextClick={() => changeStep(currentStep + 1)}
                        onSubmitClick={() => this.onSubmitClick()}
                        onCloseClick={closeImportWizard}
                    />
                </div>
                {this.state.showResurfacePrompt && (
                    <FieldResurfacePrompt {...fieldResurfaceProps} />
                )}
            </React.Fragment>
        );
    }
}
